/**
 * FolderBreadcrumb - Navegação breadcrumb para pastas
 * 
 * Mostra o caminho: Meus Arquivos > Pasta A > Pasta B
 * Cada segmento é clicável para navegar.
 * Inspirado no Google Drive.
 */

import React, { useEffect, useState } from 'react'
import {
  Box,
  Breadcrumbs,
  Chip,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha
} from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import FolderIcon from '@mui/icons-material/Folder'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import type { TranscriptionFolder } from '../../../types/folder'
import { getFolderBreadcrumb } from '../../../services/api'

interface Props {
  /** ID da pasta atual (null = raiz) */
  currentFolderId: string | null
  /** Navega para uma pasta específica */
  onNavigate: (folderId: string | null) => void
}

export default function FolderBreadcrumb({ currentFolderId, onNavigate }: Props) {
  const [breadcrumb, setBreadcrumb] = useState<TranscriptionFolder[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!currentFolderId) {
      setBreadcrumb([])
      return
    }

    setLoading(true)
    getFolderBreadcrumb(currentFolderId)
      .then(setBreadcrumb)
      .catch(() => setBreadcrumb([]))
      .finally(() => setLoading(false))
  }, [currentFolderId])

  if (!currentFolderId) return null

  return (
    <Box sx={{ mb: 1 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: 18, color: 'text.disabled' }} />}
        sx={{
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap'
          }
        }}
      >
        {/* Root / Home */}
        <Tooltip title="Voltar para a raiz das transcrições" arrow>
          <Chip
            icon={<HomeIcon sx={{ fontSize: '16px !important' }} />}
            label="Meus arquivos"
            size="small"
            variant="outlined"
            clickable
            onClick={() => onNavigate(null)}
            sx={{
              height: 28,
              fontWeight: 500,
              fontSize: '0.8rem',
              borderRadius: 1.5,
              '&:hover': {
                bgcolor: alpha('#1976d2', 0.06)
              }
            }}
          />
        </Tooltip>

        {/* Loading skeleton */}
        {loading && (
          <Skeleton width={120} height={28} sx={{ borderRadius: 1 }} />
        )}

        {/* Pastas do caminho (todas clicáveis exceto a última) */}
        {!loading && breadcrumb.map((folder, index) => {
          const isLast = index === breadcrumb.length - 1
          const folderColor = folder.color || '#90CAF9'

          if (isLast) {
            return (
              <Chip
                key={folder.id}
                icon={<FolderIcon sx={{ fontSize: '16px !important', color: `${folderColor} !important` }} />}
                label={folder.name}
                size="small"
                sx={{
                  height: 28,
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  borderRadius: 1.5,
                  bgcolor: alpha(folderColor, 0.1),
                  color: 'text.primary'
                }}
              />
            )
          }

          return (
            <Chip
              key={folder.id}
              icon={<FolderIcon sx={{ fontSize: '16px !important', color: `${folderColor} !important` }} />}
              label={folder.name}
              size="small"
              variant="outlined"
              clickable
              onClick={() => onNavigate(folder.id)}
              sx={{
                height: 28,
                fontWeight: 500,
                fontSize: '0.8rem',
                borderRadius: 1.5,
                '&:hover': {
                  bgcolor: alpha(folderColor, 0.06)
                }
              }}
            />
          )
        })}
      </Breadcrumbs>
    </Box>
  )
}
